import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Tooltip,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ShareIcon from "@mui/icons-material/Share";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublicIcon from "@mui/icons-material/Public";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { useAuth } from "../../firebase/authContext";
import {
  getUserNotes,
  deleteNote,
  toggleNoteShare,
  exportNotes,
} from "../../firebase/notesService";
import NoteEditorDialog from "./NoteEditorDialog";
import ConfirmDialog from "../ConfirmDialog";
import { cleanVerseQuoteText } from "../../bible/verseQuote";

const NotesPage = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();

  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all"); // 'all' | 'public' | 'private'
  const [selectedTag, setSelectedTag] = useState("all");
  const [selectedBook, setSelectedBook] = useState("all");

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchNotes = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setNotesLoading(true);
    }
    try {
      const list = await getUserNotes(currentUser?.uid);
      setNotes(list);
    } finally {
      setNotesLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    // Wait for Firebase auth to resolve so the first fetch uses the real uid
    // (or confirmed guest) instead of an empty signed-out snapshot.
    if (authLoading) {
      return undefined;
    }
    fetchNotes();
    const handleNotesChanged = () => fetchNotes({ silent: true });
    window.addEventListener("bible_notes_changed", handleNotesChanged);
    return () => {
      window.removeEventListener("bible_notes_changed", handleNotesChanged);
    };
  }, [fetchNotes, authLoading]);

  const pageLoading = authLoading || notesLoading;

  // Extract distinct tags and books
  const allTags = useMemo(() => {
    const set = new Set();
    notes.forEach((n) => {
      (n.tags || []).forEach((t) => set.add(t));
    });
    return Array.from(set);
  }, [notes]);

  const allBooks = useMemo(() => {
    const set = new Set();
    notes.forEach((n) => {
      if (n.bookName) set.add(n.bookName);
    });
    return Array.from(set);
  }, [notes]);

  // Filter notes
  const filteredNotes = useMemo(() => {
    return notes.filter((n) => {
      // Status filter
      if (selectedFilter === "public" && !n.isPublic) return false;
      if (selectedFilter === "private" && n.isPublic) return false;

      // Tag filter
      if (selectedTag !== "all" && !(n.tags || []).includes(selectedTag))
        return false;

      // Book filter
      if (selectedBook !== "all" && n.bookName !== selectedBook) return false;

      // Search term
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchTitle = (n.title || "").toLowerCase().includes(query);
        const matchContent = (n.content || "").toLowerCase().includes(query);
        const matchVerse = (n.verseText || "").toLowerCase().includes(query);
        const matchBook = (n.bookName || "").toLowerCase().includes(query);
        const matchTags = (n.tags || []).some((t) =>
          t.toLowerCase().includes(query)
        );
        return matchTitle || matchContent || matchVerse || matchBook || matchTags;
      }

      return true;
    });
  }, [notes, selectedFilter, selectedTag, selectedBook, searchTerm]);

  const hasActiveFilters =
    selectedFilter !== "all" ||
    selectedTag !== "all" ||
    selectedBook !== "all" ||
    Boolean(searchTerm.trim());
  const emptyNotesTitle =
    notes.length === 0 && !hasActiveFilters ? "还没有笔记" : "暂无匹配的笔记";

  const handleExport = (format) => {
    setExportAnchorEl(null);
    exportNotes(filteredNotes, format);
    setToastMsg(`已成功导出 ${filteredNotes.length} 条笔记！`);
  };

  const handleCopyShareLink = (note) => {
    const url = `${window.location.origin}/shared/notes/${note.id}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      setToastMsg("分享链接已复制到剪贴板！");
    }
  };

  const handleToggleShare = async (note) => {
    const isCurrentlySubmittedOrPublic = Boolean(
      note.isPublic || note.reviewStatus === "pending"
    );
    const nextState = !isCurrentlySubmittedOrPublic;
    await toggleNoteShare(note.id, nextState, currentUser);
    fetchNotes({ silent: true });
    setToastMsg(
      nextState
        ? "已提交公开申请，等待管理员后台审核通过后展示"
        : "已撤回公开分享，设为仅自己可见"
    );
  };

  const handleDelete = (noteId) => {
    setPendingDeleteId(noteId);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    setDeleting(true);
    try {
      await deleteNote(pendingDeleteId, currentUser);
      setToastMsg("笔记已删除");
      setPendingDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setEditorOpen(true);
  };

  if (pageLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <NoteAltIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              我的圣经灵修笔记
            </Typography>
            <Typography variant="body2" color="text.secondary">
              记录神的话语在心中的默想，支持一键导出和公开分享
            </Typography>
          </Box>
        </Box>
        <Box
          data-testid="notes-loading"
          sx={{ textAlign: "center", py: 8 }}
          aria-busy="true"
          aria-label="正在加载笔记"
        >
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            正在加载笔记...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <NoteAltIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              我的圣经灵修笔记
            </Typography>
            <Typography variant="body2" color="text.secondary">
              记录神的话语在心中的默想，支持一键导出和公开分享
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={(e) => setExportAnchorEl(e.currentTarget)}
            disabled={!filteredNotes.length}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            一键导出笔记 ({filteredNotes.length})
          </Button>

          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={() => setExportAnchorEl(null)}
            PaperProps={{ sx: { borderRadius: 2, minWidth: 220 } }}
          >
            <MenuItem onClick={() => handleExport("markdown")}>
              <ListItemText
                primary="导出为 Markdown (.md)"
                secondary="支持 Obsidian / Logseq 格式"
              />
            </MenuItem>
            <MenuItem onClick={() => handleExport("json")}>
              <ListItemText
                primary="导出为 JSON 备份 (.json)"
                secondary="完整结构化数据备份"
              />
            </MenuItem>
            <MenuItem onClick={() => handleExport("txt")}>
              <ListItemText
                primary="导出为 纯文本 (.txt)"
                secondary="便携易读纯文本格式"
              />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Stats Summary Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.015)",
          display: "flex",
          justifyContent: "space-around",
          textAlign: "center",
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            全部笔记
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.main" }}>
            {notes.length}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box>
          <Typography variant="caption" color="text.secondary">
            已公开分享
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "success.main" }}>
            {notes.filter((n) => n.isPublic).length}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box>
          <Typography variant="caption" color="text.secondary">
            涵盖书卷
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {allBooks.length} 卷
          </Typography>
        </Box>
      </Paper>

      {/* Search and Filters */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="搜索笔记标题、灵修感悟、经文摘录或标签..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
            },
          }}
        />

        {/* Filter Pills */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Chip
            label="全部"
            size="small"
            color={selectedFilter === "all" ? "primary" : "default"}
            variant={selectedFilter === "all" ? "filled" : "outlined"}
            onClick={() => setSelectedFilter("all")}
            sx={{ fontWeight: 600 }}
          />
          <Chip
            icon={<PublicIcon fontSize="small" />}
            label="公开分享"
            size="small"
            color={selectedFilter === "public" ? "primary" : "default"}
            variant={selectedFilter === "public" ? "filled" : "outlined"}
            onClick={() => setSelectedFilter("public")}
            sx={{ fontWeight: 600 }}
          />
          <Chip
            icon={<LockOutlinedIcon fontSize="small" />}
            label="仅自己可见"
            size="small"
            color={selectedFilter === "private" ? "primary" : "default"}
            variant={selectedFilter === "private" ? "filled" : "outlined"}
            onClick={() => setSelectedFilter("private")}
            sx={{ fontWeight: 600 }}
          />

          {allTags.length > 0 && (
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          )}

          {allTags.map((t) => (
            <Chip
              key={t}
              label={`#${t}`}
              size="small"
              color={selectedTag === t ? "secondary" : "default"}
              variant={selectedTag === t ? "filled" : "outlined"}
              onClick={() => setSelectedTag(selectedTag === t ? "all" : t)}
              sx={{ fontWeight: 600 }}
            />
          ))}

          {allBooks.length > 0 && (
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          )}

          {allBooks.map((b) => (
            <Chip
              key={b}
              label={b}
              size="small"
              color={selectedBook === b ? "primary" : "default"}
              variant={selectedBook === b ? "filled" : "outlined"}
              onClick={() => setSelectedBook(selectedBook === b ? "all" : b)}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
      </Box>

      {/* Notes List */}
      {filteredNotes.length > 0 ? (
        <Grid container spacing={2.5}>
          {filteredNotes.map((note) => {
            const ref = `${note.bookName || ""} 第${note.chapterNumber || 1}章${
              note.verseRange ? `:${note.verseRange}` : ""
            }`;

            return (
              <Grid item xs={12} key={note.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "box-shadow 0.2s",
                    "&:hover": {
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  {/* Top line: Reference + Status badge + Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5,
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={ref}
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/chapter/${note.chapterId}`)}
                        sx={{
                          fontWeight: 700,
                          cursor: "pointer",
                          "&:hover": { opacity: 0.85 },
                        }}
                      />
                      {note.isPublic || note.reviewStatus === "approved" ? (
                        <Chip
                          icon={<PublicIcon fontSize="small" />}
                          label="已公开"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700 }}
                        />
                      ) : note.reviewStatus === "pending" ? (
                        <Chip
                          label="审核中"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700 }}
                        />
                      ) : note.reviewStatus === "rejected" ? (
                        <Chip
                          label="未通过"
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          icon={<LockOutlinedIcon fontSize="small" />}
                          label="私密"
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {note.isPublic && (
                        <Tooltip title="复制公开分享链接">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyShareLink(note)}
                            color="primary"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip
                        title={
                          note.isPublic || note.reviewStatus === "pending"
                            ? "取消公开 / 撤回申请"
                            : "申请公开分享（需管理员审核）"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleToggleShare(note)}
                          color={
                            note.isPublic
                              ? "success"
                              : note.reviewStatus === "pending"
                                ? "warning"
                                : "default"
                          }
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="编辑笔记">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(note)}
                          color="default"
                        >
                          <EditNoteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="删除笔记">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(note.id)}
                          color="error"
                          aria-label="删除笔记"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Note Title */}
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                    {note.title}
                  </Typography>

                  {/* Scripture Quote */}
                  {note.verseText && (
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 2,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                        borderLeft: "3px solid",
                        borderLeftColor: "primary.main",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontStyle: "italic",
                          color: "text.secondary",
                          lineHeight: 1.6,
                        }}
                      >
                        {cleanVerseQuoteText(note.verseText)}
                      </Typography>
                    </Box>
                  )}

                  {/* Note Content */}
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.7,
                      whiteSpace: "pre-line",
                      color: "text.primary",
                      mb: note.reviewStatus === "rejected" && note.reviewNote ? 1 : 2,
                    }}
                  >
                    {note.content}
                  </Typography>

                  {note.reviewStatus === "rejected" && note.reviewNote && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mb: 1.5, fontWeight: 600 }}
                    >
                      未通过原因：{note.reviewNote}
                    </Typography>
                  )}

                  {/* Bottom tags & timestamp */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(note.tags || []).map((t) => (
                        <Chip
                          key={t}
                          label={`#${t}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      ))}
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      更新于：
                      {note.updatedAtIso || note.createdAtIso
                        ? new Date(
                            note.updatedAtIso || note.createdAtIso
                          ).toLocaleDateString()
                        : "近期"}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            py: 8,
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <NoteAltIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {emptyNotesTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            在阅读圣经章节时选中经文，点击「写笔记」即可记录您的第一篇灵修感悟
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate("/")}
            sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
          >
            前往选卷读经
          </Button>
        </Paper>
      )}

      {/* Note Editor Modal */}
      {editorOpen && (
        <NoteEditorDialog
          open={editorOpen}
          onClose={() => {
            setEditorOpen(false);
            setEditingNote(null);
          }}
          initialNote={editingNote}
          chapterId={editingNote?.chapterId}
          bookName={editingNote?.bookName}
          chapterNumber={editingNote?.chapterNumber}
          verseRange={editingNote?.verseRange}
          verseText={editingNote?.verseText}
          onSaved={() => {
            fetchNotes({ silent: true });
            setToastMsg("笔记已成功保存！");
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(pendingDeleteId)}
        title="删除笔记"
        message="确定要删除这条笔记吗？此操作不可撤销。"
        confirmLabel="删除"
        cancelLabel="取消"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDeleteId(null);
        }}
      />

      {/* Toast Notification */}
      <Snackbar
        open={Boolean(toastMsg)}
        autoHideDuration={3000}
        onClose={() => setToastMsg("")}
        message={toastMsg}
      />
    </Container>
  );
};

export default NotesPage;
